/**
 * AdminTabBoundary
 *
 * Wraps each admin tab's content in a React error boundary so that a render
 * error in any single tab (e.g. malformed API response, undefined field
 * access) does NOT blank the entire admin dashboard. Instead the user sees a
 * friendly error card with the option to reload just the tab — the rest of
 * the admin panel and the sidebar stay usable.
 *
 * Why this matters: a single `.toUpperCase()` on an `undefined` field would
 * otherwise propagate to React's root and white-screen the app. With this
 * boundary in place, the failure is contained, logged, and recoverable.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export class AdminTabBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[AdminTabBoundary]', this.props.tabName || 'unknown tab', error, info?.componentStack);
    // Best-effort: POST the error to the admin audit trail so we can diagnose
    // production-only crashes without asking the user to copy/paste their
    // console. Fire-and-forget — never let this rethrow into React.
    try {
      const payload = {
        message: String(error?.message || error || 'Unknown error'),
        stack: error?.stack ? String(error.stack).slice(0, 20000) : null,
        component_stack: info?.componentStack ? String(info.componentStack).slice(0, 20000) : null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        tab_name: this.props.tabName || null,
      };
      // Use fetch directly so we don't pull in the axios interceptors and
      // accidentally trip another error during reporting.
      const base = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${base}/api/admin/client-errors`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => { /* silently swallow */ });
    } catch (_e) { /* reporting must never throw */ }
  }

  reset = () => this.setState({ hasError: false, error: null });

  reloadTab = () => {
    this.reset();
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const tabLabel = this.props.tabName || 'this section';
    const message = String(this.state.error?.message || this.state.error || 'Unknown error');
    return (
      <Card className="bg-white border-amber-200 shadow-sm" data-testid="admin-tab-error">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-slate-900">
                Something went wrong loading {tabLabel}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Don&apos;t worry — your data is safe. The rest of the admin panel still works.
                Try reloading just this section. If the problem persists, contact support.
              </p>
              <details className="mt-3 text-xs text-slate-500">
                <summary className="cursor-pointer hover:text-slate-700">Technical details</summary>
                <pre className="mt-2 p-2 bg-slate-100 rounded overflow-x-auto text-xs whitespace-pre-wrap break-words">{message}</pre>
              </details>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={this.reset} variant="outline" size="sm" data-testid="admin-tab-error-retry">
              Try again
            </Button>
            <Button onClick={this.reloadTab} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="admin-tab-error-reload">
              <RefreshCw className="w-4 h-4 mr-1" />
              Reload page
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
}

export default AdminTabBoundary;
